import {
  asClippedText,
  asKind,
  asNonEmptyString,
  clip,
  COVI_EDITOR_ATTRIBUTE,
  COVI_LIST_FILTERS_ATTRIBUTE,
  COVI_LIST_SEARCH_ATTRIBUTE,
  COVI_LIST_SHOWING_ATTRIBUTE,
  COVI_LIST_SORT_ATTRIBUTE,
  COVI_LIST_TOTAL_ATTRIBUTE,
  COVI_ROW_ANCHOR_ATTRIBUTE,
  COVI_ROW_NAME_ATTRIBUTE,
  COVI_ROW_STATUS_ATTRIBUTE,
  COVI_VIEW_ATTRIBUTE,
  COVI_VIEW_LIST_ATTRIBUTE,
  FOCUS_STACK_LIMIT,
  ID_MAX_CHARS,
  LIST_META_TEXT_MAX_CHARS,
  NAME_MAX_CHARS,
  parseCoviEditorDeclaration,
  parseCoviViewDeclaration,
  ROUTE_MAX_CHARS,
  SELECTION_MAX_CHARS,
  STATUS_MAX_CHARS,
  VISIBLE_ROWS_LIMIT,
  type CoviFocusStackEntry,
  type CoviIntersectionChange,
  type CoviIntersectionObserverLike,
  type CoviViewEnvelope,
  type CoviViewKind,
  type CoviViewListMeta,
  type CoviViewPrimary,
  type CoviViewRegistry,
  type CoviViewSelection,
  type CoviViewSnapshot,
  type CoviViewVisibleRow,
  type CreateCoviViewRegistryOptions,
} from "./viewContextDeclaration"
import { getCoviViewRoute } from "./workspaceRoute"

/** Tracks declared entities, viewport rows, selection, and recent focus. */
function defaultIntersectionObserverFactory(
  onChange: (changes: CoviIntersectionChange[]) => void,
): CoviIntersectionObserverLike | null {
  if (typeof IntersectionObserver === "undefined") {
    return null
  }
  // threshold 0: a row counts as "on screen" the moment any of it is —
  // partially-scrolled edge rows are still things the user can see.
  return new IntersectionObserver(
    (entries) =>
      onChange(
        entries.map((entry) => ({
          target: entry.target,
          isIntersecting: entry.isIntersecting,
        })),
      ),
    { threshold: 0 },
  )
}

/** DOM order (top of page first) so `visible` reads the way the screen does. */
function byDocumentPosition(a: Element, b: Element): number {
  const relation = a.compareDocumentPosition(b)
  if (relation & Node.DOCUMENT_POSITION_FOLLOWING) {
    return -1
  }
  if (relation & Node.DOCUMENT_POSITION_PRECEDING) {
    return 1
  }
  return 0
}

export function createCoviViewRegistry(
  options: CreateCoviViewRegistryOptions = {},
): CoviViewRegistry {
  const now = options.now ?? (() => Date.now())
  const createIntersectionObserver =
    options.createIntersectionObserver ?? defaultIntersectionObserverFactory

  let orgSlug: string | null = null
  let started = false
  let intersectionObserver: CoviIntersectionObserverLike | null = null
  let mutationObserver: MutationObserver | null = null
  let rescanQueued = false

  /** Row anchors currently handed to the IntersectionObserver. */
  const observedRows = new Set<Element>()
  /** The subset of observed rows currently intersecting the viewport. */
  const visibleRows = new Set<Element>()

  /** The current page's declared primary entity (null on undeclared pages). */
  let currentPrimary: CoviViewPrimary | null = null
  /**
   * Recently-viewed primaries, most recent first, EXCLUDING the current one.
   * `lastSeenAt` is stamped when the entity STOPPED being the primary — i.e.
   * when the user navigated away — so `viewed_s_ago` reads "you were looking
   * at this N seconds ago" regardless of how long it was open.
   */
  let focusHistory: Array<{
    kind: CoviViewKind
    id: string
    name?: string
    lastSeenAt: number
  }> = []

  const handleIntersections = (changes: CoviIntersectionChange[]) => {
    for (const change of changes) {
      if (change.isIntersecting) {
        visibleRows.add(change.target)
      } else {
        visibleRows.delete(change.target)
      }
    }
  }

  /**
   * Read and parse the last `[attribute]` declaration in document order:
   * portaled drawers render later in the DOM, so the last one wins.
   */
  const readDeclaration = <T>(
    attribute: string,
    parse: (raw: string | null) => T | null,
  ): T | null => {
    const declarations = document.querySelectorAll(`[${attribute}]`)
    const element =
      declarations.length > 0 ? declarations[declarations.length - 1] : null
    return element ? parse(element.getAttribute(attribute)) : null
  }

  const samePrimary = (
    a: CoviViewPrimary | null,
    b: CoviViewPrimary | null,
  ): boolean => a === b || (!!a && !!b && a.kind === b.kind && a.id === b.id)

  /**
   * Record a primary-entity transition in the focus stack. Consecutive
   * re-reads of the same entity only refresh its fields (name/status often
   * arrive after the first render); a genuine change pushes the outgoing
   * entity onto the stack (deduped, capped) and evicts the incoming one.
   */
  const notePrimary = (next: CoviViewPrimary | null) => {
    if (samePrimary(next, currentPrimary)) {
      currentPrimary = next
      return
    }
    const outgoing = currentPrimary
    if (outgoing) {
      focusHistory = [
        {
          kind: outgoing.kind,
          id: outgoing.id,
          ...(outgoing.name ? { name: outgoing.name } : {}),
          lastSeenAt: now(),
        },
        ...focusHistory.filter(
          (entry) => entry.kind !== outgoing.kind || entry.id !== outgoing.id,
        ),
      ]
    }
    if (next) {
      focusHistory = focusHistory.filter(
        (entry) => entry.kind !== next.kind || entry.id !== next.id,
      )
    }
    focusHistory = focusHistory.slice(0, FOCUS_STACK_LIMIT)
    currentPrimary = next
  }

  const syncPrimary = () => {
    notePrimary(readDeclaration(COVI_VIEW_ATTRIBUTE, parseCoviViewDeclaration))
  }

  const syncRows = () => {
    const anchors = new Set<Element>(
      Array.from(
        document.querySelectorAll(
          `[${COVI_VIEW_LIST_ATTRIBUTE}] [${COVI_ROW_ANCHOR_ATTRIBUTE}]`,
        ),
      ),
    )
    for (const element of Array.from(observedRows)) {
      if (!anchors.has(element)) {
        intersectionObserver?.unobserve(element)
        observedRows.delete(element)
        visibleRows.delete(element)
      }
    }
    for (const element of anchors) {
      if (!observedRows.has(element)) {
        observedRows.add(element)
        intersectionObserver?.observe(element)
      }
    }
  }

  const rescan = () => {
    if (!started || typeof document === "undefined") {
      return
    }
    syncPrimary()
    syncRows()
  }

  /** Coalesce mutation bursts (React commits mutate in batches) into one rescan. */
  const scheduleRescan = () => {
    if (rescanQueued) {
      return
    }
    rescanQueued = true
    queueMicrotask(() => {
      rescanQueued = false
      rescan()
    })
  }

  const start = () => {
    if (
      started ||
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.body
    ) {
      return
    }
    started = true
    intersectionObserver = createIntersectionObserver(handleIntersections)
    if (typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(scheduleRescan)
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [
          COVI_VIEW_ATTRIBUTE,
          COVI_VIEW_LIST_ATTRIBUTE,
          COVI_ROW_ANCHOR_ATTRIBUTE,
        ],
      })
    }
    rescan()
  }

  const stop = () => {
    started = false
    mutationObserver?.disconnect()
    mutationObserver = null
    intersectionObserver?.disconnect()
    intersectionObserver = null
    observedRows.clear()
    visibleRows.clear()
  }

  const setOrgSlug = (slug: string | null) => {
    if (slug === orgSlug) {
      return
    }
    orgSlug = slug
    // Recent-view memory never crosses orgs: ids from another org would be
    // meaningless (or misleading) to Sofia in the new org's scope.
    focusHistory = []
    currentPrimary = null
    if (started) {
      rescan()
    }
  }

  const captureRoute = () =>
    getCoviViewRoute(window.location.pathname || "/", orgSlug, ROUTE_MAX_CHARS)

  const collectVisible = (): {
    rows: CoviViewVisibleRow[]
    firstRowElement: Element | null
  } => {
    const connected: Element[] = []
    for (const element of Array.from(visibleRows)) {
      // Prune rows that unmounted since the observer last reported them.
      if (element.isConnected) {
        connected.push(element)
      } else {
        visibleRows.delete(element)
      }
    }
    connected.sort(byDocumentPosition)
    const rows: CoviViewVisibleRow[] = []
    const seen = new Set<string>()
    let firstRowElement: Element | null = null
    for (const element of connected) {
      if (rows.length >= VISIBLE_ROWS_LIMIT) {
        break
      }
      const id = asNonEmptyString(
        element.getAttribute(COVI_ROW_ANCHOR_ATTRIBUTE),
      )
      if (!id) {
        continue
      }
      const container = element.closest(`[${COVI_VIEW_LIST_ATTRIBUTE}]`)
      const kind = asKind(container?.getAttribute(COVI_VIEW_LIST_ATTRIBUTE))
      if (!kind) {
        continue
      }
      const dedupeKey = `${kind}:${id}`
      if (seen.has(dedupeKey)) {
        continue
      }
      seen.add(dedupeKey)
      const row: CoviViewVisibleRow = { kind, id: clip(id, ID_MAX_CHARS) }
      const name = asClippedText(
        element.getAttribute(COVI_ROW_NAME_ATTRIBUTE),
        NAME_MAX_CHARS,
      )
      if (name) {
        row.name = name
      }
      const status = asClippedText(
        element.getAttribute(COVI_ROW_STATUS_ATTRIBUTE),
        STATUS_MAX_CHARS,
      )
      if (status) {
        row.status = status
      }
      if (!firstRowElement) {
        firstRowElement = element
      }
      rows.push(row)
    }
    return { rows, firstRowElement }
  }

  const collectListMeta = (
    firstRowElement: Element | null,
  ): CoviViewListMeta | null => {
    // The list the user is actually looking at: the container of the topmost
    // visible row; else the page's first declared list (empty/filtered table).
    const container =
      firstRowElement?.closest(`[${COVI_VIEW_LIST_ATTRIBUTE}]`) ??
      document.querySelector(`[${COVI_VIEW_LIST_ATTRIBUTE}]`)
    if (!container) {
      return null
    }
    const meta: CoviViewListMeta = {}
    const rawTotal = container.getAttribute(COVI_LIST_TOTAL_ATTRIBUTE)
    if (rawTotal !== null && rawTotal.trim().length > 0) {
      const total = Number(rawTotal)
      if (Number.isFinite(total) && total >= 0) {
        meta.total = total
      }
    }
    const textAttrs: Array<[keyof Omit<CoviViewListMeta, "total">, string]> = [
      ["filters", COVI_LIST_FILTERS_ATTRIBUTE],
      ["sort", COVI_LIST_SORT_ATTRIBUTE],
      ["search", COVI_LIST_SEARCH_ATTRIBUTE],
      ["showing", COVI_LIST_SHOWING_ATTRIBUTE],
    ]
    for (const [key, attribute] of textAttrs) {
      const value = asClippedText(
        container.getAttribute(attribute),
        LIST_META_TEXT_MAX_CHARS,
      )
      if (value) {
        meta[key] = value
      }
    }
    return Object.keys(meta).length > 0 ? meta : null
  }

  const collectSelection = (): CoviViewSelection | null => {
    const domSelection =
      typeof window.getSelection === "function" ? window.getSelection() : null
    // Collapse runs of whitespace: selections spanning table cells arrive as
    // tab/newline soup that wastes the char budget without adding signal.
    const text = (domSelection?.toString() ?? "").replace(/\s+/g, " ").trim()
    if (!text) {
      return null
    }
    const selection: CoviViewSelection = {
      text: clip(text, SELECTION_MAX_CHARS),
    }
    const anchorNode = domSelection?.anchorNode ?? null
    const anchorElement =
      anchorNode === null
        ? null
        : anchorNode.nodeType === Node.ELEMENT_NODE
          ? (anchorNode as Element)
          : anchorNode.parentElement
    if (!anchorElement) {
      return selection
    }
    // Provenance, most specific first: the row anchor the selection sits in
    // (kind from its declared list container), else the enclosing primary
    // declaration. Absent both, the text still rides — just unattributed.
    const rowElement = anchorElement.closest(`[${COVI_ROW_ANCHOR_ATTRIBUTE}]`)
    if (rowElement) {
      const id = asNonEmptyString(
        rowElement.getAttribute(COVI_ROW_ANCHOR_ATTRIBUTE),
      )
      if (id) {
        selection.id = clip(id, ID_MAX_CHARS)
        const kind = asKind(
          rowElement
            .closest(`[${COVI_VIEW_LIST_ATTRIBUTE}]`)
            ?.getAttribute(COVI_VIEW_LIST_ATTRIBUTE),
        )
        if (kind) {
          selection.kind = kind
        } else {
          // A bare anchor outside any list (e.g. a detail page's own glow
          // anchor): attribute it via the enclosing primary declaration when
          // the ids agree.
          const primary = parseCoviViewDeclaration(
            rowElement
              .closest(`[${COVI_VIEW_ATTRIBUTE}]`)
              ?.getAttribute(COVI_VIEW_ATTRIBUTE) ?? null,
          )
          if (primary && primary.id === selection.id) {
            selection.kind = primary.kind
          }
        }
        return selection
      }
    }
    const primaryElement = anchorElement.closest(`[${COVI_VIEW_ATTRIBUTE}]`)
    const primary = primaryElement
      ? parseCoviViewDeclaration(
          primaryElement.getAttribute(COVI_VIEW_ATTRIBUTE),
        )
      : null
    if (primary) {
      selection.kind = primary.kind
      selection.id = primary.id
    }
    return selection
  }

  const serializeFocusStack = (): CoviFocusStackEntry[] => {
    const at = now()
    return focusHistory.slice(0, FOCUS_STACK_LIMIT).map((entry) => {
      const serialized: CoviFocusStackEntry = {
        kind: entry.kind,
        id: entry.id,
        viewed_s_ago: Math.max(0, Math.round((at - entry.lastSeenAt) / 1000)),
      }
      if (entry.name) {
        serialized.name = entry.name
      }
      return serialized
    })
  }

  const snapshot = (): CoviViewSnapshot => {
    try {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return { view: null, focus_stack: [] }
      }
      // Send-time freshness: a primary that mounted since the last mutation
      // flush (or a route change the observers haven't seen) is picked up now.
      rescan()
      const view: CoviViewEnvelope = { route: captureRoute() }
      if (currentPrimary) {
        view.primary = { ...currentPrimary }
      }
      // The editor declaration is read fresh at send time — DOM-derived like the route.
      const editor = readDeclaration(
        COVI_EDITOR_ATTRIBUTE,
        parseCoviEditorDeclaration,
      )
      if (editor) {
        view.editor = editor
      }
      const { rows, firstRowElement } = collectVisible()
      if (rows.length > 0) {
        view.visible = rows
      }
      const listMeta = collectListMeta(firstRowElement)
      if (listMeta) {
        view.list_meta = listMeta
      }
      const selection = collectSelection()
      if (selection) {
        view.selection = selection
      }
      return { view, focus_stack: serializeFocusStack() }
    } catch {
      // View capture is best-effort by contract: any failure degrades to a
      // vision-less turn, never a blocked send.
      return { view: null, focus_stack: [] }
    }
  }

  const reset = () => {
    currentPrimary = null
    focusHistory = []
    visibleRows.clear()
  }

  return { start, stop, setOrgSlug, rescan, snapshot, reset }
}
